import { ReactNode } from "react";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { Card, CardContent } from "@/components/ui/card";
import {
  Database,
  Inbox,
  Sparkles,
  Users,
  History,
  BarChart3,
  ChevronDown,
  UserPlus,
  ClipboardCheck,
  IdCard,
  Search,
  HeartHandshake,
  Bell,
  Eye,
  Check,
  Lock,
  Unlock,
  ImageIcon,
  LucideIcon,
} from "lucide-react";

function FlowNode({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="w-full shadow-soft">
      <CardContent className="flex items-start gap-4 p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-white">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="font-semibold leading-tight">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{children}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function FlowArrow() {
  return (
    <div className="flex justify-center py-1" aria-hidden="true">
      <ChevronDown className="h-5 w-5 text-muted-foreground/60" />
    </div>
  );
}

function ScreenshotFrame({ label }: { label: string }) {
  return (
    <div className="mt-3 flex aspect-[16/10] w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/40 text-muted-foreground">
      <ImageIcon className="h-7 w-7 opacity-60" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

const tools: { icon: LucideIcon; name: string; blurb: string; shot: string }[] = [
  {
    icon: Database,
    name: "All Profiles",
    blurb:
      "Browse and search every active candidate. Filter by gender, age, and location, open a profile for the full picture, and use Create Profile to set one up for a candidate who needs a hand.",
    shot: "All Profiles screenshot",
  },
  {
    icon: Inbox,
    name: "Join Requests",
    blurb:
      "Your queue of new applications. Read each person's community connection and references, then approve to create their profile or decline.",
    shot: "Join Requests screenshot",
  },
  {
    icon: Sparkles,
    name: "Smart Matcher",
    blurb:
      "Pick two candidates and get an AI compatibility read: a score plus the reasoning behind it, there to back your judgment rather than replace it.",
    shot: "Smart Matcher screenshot",
  },
  {
    icon: Users,
    name: "Mutual Matches",
    blurb:
      "Surfaces pairs whose stated preferences point at each other, so you can spot promising introductions you might have missed.",
    shot: "Mutual Matches screenshot",
  },
  {
    icon: History,
    name: "Match History",
    blurb:
      "Every match you have created and where it stands: pending, accepted, declined, or accepted by both sides.",
    shot: "Match History screenshot",
  },
  {
    icon: BarChart3,
    name: "Analytics",
    blurb:
      "A read on your pipeline at a glance: match counts, acceptance rates, and overall activity.",
    shot: "Analytics screenshot",
  },
];

export default function MatchmakerGuide() {
  return (
    <MatchmakerLayout title="Matchmaker Guide">
      <div className="mx-auto max-w-3xl space-y-14 p-6">
        <section className="space-y-4">
          <h1 className="text-3xl font-bold">Welcome to the workbench</h1>
          <p className="text-muted-foreground">
            As a matchmaker you are the trust anchor for the people you serve.
            Candidates never browse each other and there is no public pool. You
            get to know each person, decide who should meet whom, and choose
            when to make an introduction.
          </p>
          <p className="text-muted-foreground">
            The platform keeps that personal. It holds profiles, references, and
            preferences in one place so you can work at a scale that memory and
            chat threads never could. One rule sits underneath everything: a
            candidate's contact details stay hidden until both people accept the
            match you make. This guide walks you through how a candidate moves
            through the system and what each tool in your sidebar does.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-bold">How it works</h2>
          <div>
            <FlowNode icon={UserPlus} title="A candidate requests to join">
              They submit their details, community connection, and references
              through the public Request to Join page.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={ClipboardCheck} title="You review the request">
              The application lands in your Join Requests queue. You read their
              connection and references, then approve or decline.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={IdCard} title="A profile exists">
              Once you approve, the candidate has a profile you work with from
              then on in All Profiles.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Search} title="You find a pairing">
              Use Smart Matcher for AI compatibility scoring, or work through All
              Profiles and Mutual Matches until you land on a pairing you believe
              in.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={HeartHandshake} title="You create the match">
              You make the introduction inside the app.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Bell} title="Both candidates are notified">
              Each person gets an email letting them know a match is waiting for
              their response.
            </FlowNode>
          </div>
          <p className="text-sm text-muted-foreground">
            The candidate journey, from request to join through to a match you
            put in front of two people.
          </p>
        </section>

        <section className="space-y-6">
          <h2 className="text-2xl font-bold">The workbench</h2>
          <p className="text-muted-foreground">
            Everything you need sits in the sidebar. Here is what each item is
            for.
          </p>
          <div className="space-y-8">
            {tools.map((tool) => (
              <div key={tool.name}>
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <tool.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold">{tool.name}</h3>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {tool.blurb}
                </p>
                <ScreenshotFrame label={tool.shot} />
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-bold">The match handshake</h2>
          <div>
            <FlowNode icon={HeartHandshake} title="You create the match">
              Two candidates are now linked by the introduction you made.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Eye} title="Each sees it with limited info">
              Both people view the match with a partial picture and no contact
              details.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Check} title="Each accepts or declines">
              They respond on their own, without seeing what the other side
              chose.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Lock} title="One accept is not enough">
              While only one side has accepted, contact details stay hidden for
              both.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={Unlock} title="Both accept, contact is revealed">
              Only when both say yes do the two of them see each other's contact
              details.
            </FlowNode>
            <FlowArrow />
            <FlowNode icon={History} title="You follow it in Match History">
              The status stays visible to you from the moment you create the
              match.
            </FlowNode>
          </div>
          <p className="text-sm text-muted-foreground">
            The handshake that protects everyone: contact is shared only on a
            mutual yes.
          </p>
        </section>

        <section className="rounded-lg border bg-muted/30 p-6">
          <h2 className="mb-2 text-xl font-bold">Where this leaves you</h2>
          <p className="text-muted-foreground">
            This guide gets you most of the way there. Your live meeting with
            current matchmakers picks up from here and finishes the job, so come
            with the questions this leaves open.
          </p>
        </section>
      </div>
    </MatchmakerLayout>
  );
}
